import React, { useState, useEffect, useRef } from 'react';
import { Trash2, RefreshCw, Terminal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { LogEntry } from '../types';

const LEVEL_CLASSES: Record<LogEntry['level'], string> = {
  error: 'bg-red-100 text-red-700',
  warn:  'bg-amber-100 text-amber-700',
  debug: 'bg-blue-100 text-blue-700',
  log:   'bg-slate-100 text-slate-600',
};

const LEVEL_TEXT_CLASSES: Record<LogEntry['level'], string> = {
  error: 'text-red-700',
  warn:  'text-amber-700',
  debug: 'text-blue-700',
  log:   'text-slate-800',
};

const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all',   label: 'Todos' },
  { value: 'log',   label: 'Log' },
  { value: 'warn',  label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'debug', label: 'Debug' },
];

const DebugConsolePage: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [isDebugEnabled, setIsDebugEnabled] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkDebug = () => {
      const labsSettings = localStorage.getItem('labs_settings');
      console.debug('[DebugConsolePage] Checking debug settings:', labsSettings);
      if (labsSettings) {
        try {
          const s = JSON.parse(labsSettings);
          const debugEnabled = !!s.debug;
          console.debug('[DebugConsolePage] Debug enabled:', debugEnabled, 'settings:', s);
          setIsDebugEnabled(debugEnabled);
        } catch (e) {
          console.error('[DebugConsolePage] Error parsing labs settings:', e);
          setIsDebugEnabled(false);
        }
      } else {
        console.debug('[DebugConsolePage] No labs settings found');
        setIsDebugEnabled(false);
      }
    };
    checkDebug();
    const timeoutId = setTimeout(checkDebug, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    console.debug('[DebugConsolePage] isDebugEnabled changed:', isDebugEnabled);
    if (!isDebugEnabled) {
      console.debug('[DebugConsolePage] Redirecting to home - debug not enabled');
      navigate('/');
    }
  }, [isDebugEnabled, navigate]);

  useEffect(() => {
    if (!isDebugEnabled) return;

    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    const originalDebug = console.debug;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addLog = (level: LogEntry['level'], ...args: any[]) => {
      const logEntry: LogEntry = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
        level,
        message: args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' '),
        args,
      };
      setLogs(prev => [...prev, logEntry]);
    };

    console.log   = (...args) => { originalLog(...args);   addLog('log',   ...args); };
    console.warn  = (...args) => { originalWarn(...args);  addLog('warn',  ...args); };
    console.error = (...args) => { originalError(...args); addLog('error', ...args); };
    console.debug = (...args) => { originalDebug(...args); addLog('debug', ...args); };

    return () => {
      console.log   = originalLog;
      console.warn  = originalWarn;
      console.error = originalError;
      console.debug = originalDebug;
    };
  }, [isDebugEnabled]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const clearLogs = () => setLogs([]);

  const refreshDebugCheck = () => {
    const labsSettings = localStorage.getItem('labs_settings');
    console.debug('[DebugConsolePage] Manual refresh - labs settings:', labsSettings);
    if (labsSettings) {
      try {
        const s = JSON.parse(labsSettings);
        const debugEnabled = !!s.debug;
        console.debug('[DebugConsolePage] Manual refresh - debug enabled:', debugEnabled);
        setIsDebugEnabled(debugEnabled);
      } catch (e) {
        console.error('[DebugConsolePage] Manual refresh - error parsing settings:', e);
        setIsDebugEnabled(false);
      }
    } else {
      setIsDebugEnabled(false);
    }
  };

  const filteredLogs = logs.filter(log => filterLevel === 'all' || log.level === filterLevel);

  if (!isDebugEnabled) {
    return null;
  }

  return (
    <main className="flex h-screen flex-col" id="main-content" role="main" aria-label="Console de Debug">
      {/* toolbar */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <Terminal className="h-4 w-4 text-slate-500" aria-hidden="true" />
        <h1 className="flex-1 text-sm font-semibold text-slate-900">Console de Debug</h1>
        <select
          value={filterLevel}
          onChange={e => setFilterLevel(e.target.value)}
          aria-label="Filtrar por nível"
          className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
        >
          {FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span className="min-w-[3rem] text-right text-xs text-slate-400">
          {filteredLogs.length} logs
        </span>
        <Button variant="ghost" size="icon" onClick={refreshDebugCheck} aria-label="Recarregar debug" className="h-8 w-8">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button variant="ghost" size="icon" onClick={clearLogs} aria-label="Limpar logs" className="h-8 w-8">
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {/* log list */}
      <div className="flex-1 overflow-y-auto bg-slate-50 p-2 font-mono text-xs">
        {filteredLogs.length === 0 ? (
          <p className="mt-8 text-center text-slate-400">Nenhum log encontrado</p>
        ) : (
          filteredLogs.map(log => (
            <div
              key={log.id}
              className="mb-1 rounded-lg border border-slate-100 bg-white p-2"
            >
              <div className="mb-1 flex items-center gap-2">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    LEVEL_CLASSES[log.level],
                  )}
                >
                  {log.level}
                </span>
                <span className="text-slate-400">
                  {log.timestamp.toLocaleTimeString()}
                </span>
              </div>
              <pre
                className={cn(
                  'm-0 whitespace-pre-wrap break-all leading-relaxed',
                  LEVEL_TEXT_CLASSES[log.level],
                )}
              >
                {log.message}
              </pre>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </main>
  );
};

export default DebugConsolePage;
